import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canAccessModule } from "../lib/access";
import {
  type AssetId,
  assetIdSchema,
  type CustomerId,
  type FacilityId,
} from "../lib/workIds";

export type AssetTreeNode = {
  assetTreeId: number;
  /**
   * Branded external Asset identifier, needed by integrations (such as
   * Work Orders) that must reference this asset by its public ID rather
   * than `assetTreeId`. Optional so it degrades gracefully if a node is
   * ever returned without it.
   */
  assetId?: AssetId;
  name: string;
  code: string;
  assetTypeId?: number | null;
  assetTypeName?: string | null;
  kind?: "ASSET" | "LOCATION" | null;
  sapFunctionalLocationId?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  locationCode?: string | null;
  locationName?: string | null;
  children: AssetTreeNode[];
};

type AssetTreeNodeInput = Omit<AssetTreeNode, "assetId" | "children"> & {
  assetId?: string;
  children: AssetTreeNodeInput[];
};

const assetTreeNodeSchema: z.ZodType<
  AssetTreeNode,
  z.ZodTypeDef,
  AssetTreeNodeInput
> = z.lazy(() =>
  z.object({
    assetTreeId: z.number().int().positive(),
    assetId: assetIdSchema.optional(),
    name: z.string(),
    code: z.string(),
    assetTypeId: z.number().int().positive().nullable().optional(),
    assetTypeName: z.string().nullable().optional(),
    kind: z.enum(["ASSET", "LOCATION"]).nullable().optional(),
    sapFunctionalLocationId: z.string().nullable().optional(),
    manufacturer: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    locationCode: z.string().nullable().optional(),
    locationName: z.string().nullable().optional(),
    children: z.array(assetTreeNodeSchema),
  }),
);

const assetTreeResponseSchema = z.object({
  nodes: z.array(assetTreeNodeSchema),
});

export type AssetTreeResponse = z.infer<typeof assetTreeResponseSchema>;

async function fetchAssetTree(
  customerId: CustomerId,
  facilityId: FacilityId,
): Promise<AssetTreeResponse> {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["asset-tree"].$get({ param: { customerId, facilityId } });
  if (!response.ok) {
    throw new Error(`Failed to fetch asset tree: ${response.status}`);
  }
  return assetTreeResponseSchema.parse(await response.json());
}

export function useAssetTreeQuery(options?: { enabled?: boolean }) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useQuery({
    queryKey: ["asset-tree", selectedCustomer?.id, selectedFacility?.id],
    queryFn: () => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchAssetTree(selectedCustomer.id, selectedFacility.id);
    },
    enabled: Boolean(
      selectedCustomer &&
        selectedFacility &&
        canAccessModule(selectedFacility, "ASSETS") &&
        (options?.enabled ?? true),
    ),
    meta: { errorMessage: "Failed to load the asset tree." },
  });
}
